/**
 * Display names for the distinct physical sites assigned to each template lineage across every
 * environment's assignment rows. A site materialised in two environments is one site, matching the
 * publish dialog. Templates with no rows get an empty array so a finished read is distinguishable
 * from an unread map (the caller leaves the map unset when the read never completed).
 */
export function namesAssignedSitesByTemplateKey(perEnvironmentAssignments, consentFiles, siteIdByEnvironmentSiteId, labelByEnvironmentSiteId) {
    const labelsByTemplateKey = new Map();
    for (const environmentAssignments of perEnvironmentAssignments) {
        for (const assignment of environmentAssignments) {
            const siteId = siteIdByEnvironmentSiteId.get(assignment.environmentSiteId);
            const siteKey = siteId === undefined ? `environment-site:${assignment.environmentSiteId}` : `site:${siteId}`;
            const label = labelByEnvironmentSiteId.get(assignment.environmentSiteId) || siteKey;
            const labels = labelsByTemplateKey.get(assignment.consentTemplateKey);
            if (labels) {
                if (!labels.has(siteKey)) {
                    labels.set(siteKey, label);
                }
            }
            else {
                labelsByTemplateKey.set(assignment.consentTemplateKey, new Map([[siteKey, label]]));
            }
        }
    }
    const names = new Map();
    for (const file of consentFiles) {
        if (!names.has(file.templateKey)) {
            const labels = labelsByTemplateKey.get(file.templateKey);
            names.set(file.templateKey, labels ? [...labels.values()].sort((left, right) => left.localeCompare(right)) : []);
        }
    }
    return names;
}
/**
 * Counts the sites named above. Derived from `namesAssignedSitesByTemplateKey` so the count column
 * and the names cell cannot disagree. An empty label map is enough: labels fall back to the site
 * key, and the count is the number of names.
 */
export function countAssignedSitesByTemplateKey(perEnvironmentAssignments, consentFiles, siteIdByEnvironmentSiteId) {
    const names = namesAssignedSitesByTemplateKey(perEnvironmentAssignments, consentFiles, siteIdByEnvironmentSiteId, new Map());
    return new Map([...names].map(([templateKey, siteNames]) => [templateKey, siteNames.length]));
}
/**
 * Checkbox edits replace saved rows for this template in the environments the builder touched.
 * An environment absent from `pendingByEnv` keeps its saved rows. An empty list clears that
 * environment. Other templates in the same environment stay, because the draft edits one template.
 */
export function applyPendingTemplateAssignments(perEnvironmentAssignments, environmentIds, templateKey, pendingByEnv) {
    if (!pendingByEnv) {
        return perEnvironmentAssignments;
    }
    return perEnvironmentAssignments.map((rows, index) => {
        const environmentId = environmentIds[index];
        if (environmentId === undefined || !Object.hasOwn(pendingByEnv, environmentId)) {
            return rows;
        }
        const kept = rows.filter((row) => row.consentTemplateKey !== templateKey);
        const added = (pendingByEnv[environmentId] ?? []).map((environmentSiteId) => ({
            id: `pending:${environmentId}:${environmentSiteId}`,
            environmentSiteId,
            consentTemplateKey: templateKey,
        }));
        return [...kept, ...added];
    });
}
/**
 * Computes the new full assignment list for the current environment after a user
 * changes a template's site selection in the ICFs view.
 *
 * `current` must already be scoped to the current environment. Assignments for this
 * template to sites **outside** the visible environment (none, in practice, since
 * `current` is per-environment) are preserved defensively.
 */
export function replaceLinksForDocument(current, consentTemplateKey, selectedEnvironmentSiteIds, environmentSiteIds) {
    const scope = new Set(environmentSiteIds);
    const selected = new Set(selectedEnvironmentSiteIds);
    const kept = current.filter((a) => {
        if (a.consentTemplateKey !== consentTemplateKey) {
            return true;
        }
        if (!scope.has(a.environmentSiteId)) {
            return true;
        }
        return selected.has(a.environmentSiteId);
    });
    const existingSiteIds = new Set(kept.filter((a) => a.consentTemplateKey === consentTemplateKey).map((a) => a.environmentSiteId));
    const additions = [];
    for (const environmentSiteId of selectedEnvironmentSiteIds) {
        if (!existingSiteIds.has(environmentSiteId)) {
            additions.push({
                id: `pending:${environmentSiteId}:${consentTemplateKey}`,
                consentTemplateKey,
                environmentSiteId,
            });
        }
    }
    return [...kept, ...additions];
}
/**
 * Computes the new full assignment list for the current environment after a user
 * changes the template selection for one site in the Sites view.
 *
 * Links to templates **outside** `consentTemplateKeysInScope` (e.g. stale/archived keys
 * that may still exist in state) are preserved so we don't blow them away.
 */
export function replaceLinksForSite(current, environmentSiteId, selectedConsentTemplateKeys, consentTemplateKeysInScope) {
    const scope = new Set(consentTemplateKeysInScope);
    const selected = new Set(selectedConsentTemplateKeys);
    const kept = current.filter((a) => {
        if (a.environmentSiteId !== environmentSiteId) {
            return true;
        }
        if (!scope.has(a.consentTemplateKey)) {
            return true;
        }
        return selected.has(a.consentTemplateKey);
    });
    const existingTemplateKeys = new Set(kept.filter((a) => a.environmentSiteId === environmentSiteId).map((a) => a.consentTemplateKey));
    const additions = [];
    for (const consentTemplateKey of selectedConsentTemplateKeys) {
        if (!existingTemplateKeys.has(consentTemplateKey)) {
            additions.push({
                id: `pending:${environmentSiteId}:${consentTemplateKey}`,
                consentTemplateKey,
                environmentSiteId,
            });
        }
    }
    return [...kept, ...additions];
}
